import type { MouseEventHandler } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { FontValue } from '@elfsight/configurator-contracts';
import { useWindowWidth } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import type { SubmitActionType } from '@elfsight/widget-form-builder/configurator';
import { WidgetBackground } from '@elfsight/widget-ui';
import { openWindowByLink, useIsMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { meta } from '../../constants/meta';
import { StopDisplayTriggerTypes, useBarState } from '../bar-state-provider';
import { TABLET_BREAKPOINT } from '../constants';
import { ActionElement } from './action-element';
import { CloseButton } from './close-button';
import { Message } from './message';
import { FREE_LINK_HEIGHT, StyledFreeLink } from './styled-free-link';
import { VisualElement } from './visual-element';

const VERTICAL_PADDING = 10;

export const BarElements = () => {
  const {
    layout,
    background,
    closeButton,
    link,
    font,
    editMode,
    displayFreeLink,
    ...settings
  } = useSettings();

  const { stopDisplayTrigger, isOpen } = useBarState();

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const [submitMessageHeight, setSubmitMessageHeight] = useState(0);

  const windowWidth = useWindowWidth();
  const flexWrap = windowWidth <= TABLET_BREAKPOINT;
  const isMobile = useIsMobile();

  const clickHandler: MouseEventHandler = (e) => {
    e.stopPropagation();
    if (link.value) {
      stopDisplayTrigger(StopDisplayTriggerTypes.CLICKEDBAR);
      openWindowByLink(e, {
        ...link,
        target: editMode ? '_blank' : link.target
      });
    }
  };

  const onFormSubmit = (submitAction: SubmitActionType) =>
    submitAction === 'message' && setIsFormSubmitted(true);
  const onBarClose = () => setIsFormSubmitted(false);

  const [wrapperRef] = useRect(({ height }) => setBarHeight(height), {
    resize: true
  });

  const isInline = layout === 'inline';
  const padding =
    displayFreeLink && !isInline
      ? VERTICAL_PADDING + FREE_LINK_HEIGHT
      : VERTICAL_PADDING;

  const [formRef] = useRect(
    ({ height }) => setSubmitMessageHeight(height + 2 * padding),
    {
      resize: true
    }
  );

  const shadowSettings = {
    'floating-bottom': '0 -1px 4px rgba(0,0,0,0.25)',
    'floating-top': '0 1px 4px rgba(0,0,0,0.25)',
    inline: '',
    static: ''
  }[layout];

  const submittedFormHeight =
    barHeight > submitMessageHeight ? barHeight : submitMessageHeight;

  return (
    <Wrapper ref={wrapperRef} _isOpen={isOpen}>
      <Deactivation2 {...settings} appAlias={meta.alias}>
        <WidgetBackground
          {...background}
          style={{ boxShadow: shadowSettings }}
          height={isFormSubmitted ? submittedFormHeight : undefined}
        >
          <Paddings
            $padding={padding}
            $isBottomLayout={layout === 'floating-bottom'}
          >
            <ElementsWrapper
              onClick={clickHandler}
              _hasLink={!!link?.value}
              _flexWrap={flexWrap}
              _font={font}
              _isMobile={isMobile}
              _closeButton={closeButton}
            >
              <VisualElement isFormSubmitted={isFormSubmitted} />
              <Message isFormSubmitted={isFormSubmitted} />
              <ActionElement ref={formRef} onFormSubmit={onFormSubmit} />
              <CloseButton barHeight={barHeight} onClose={onBarClose} />
            </ElementsWrapper>
            <StyledFreeLink />
          </Paddings>
        </WidgetBackground>
      </Deactivation2>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ _isOpen?: boolean }>`
  ${({ _isOpen }) => !_isOpen && 'display: none !important;'}
`;

const Paddings = styled.div<{ $padding: number; $isBottomLayout: boolean }>`
  ${({ $padding, $isBottomLayout }) => css`
    padding-block: ${$isBottomLayout
      ? `${VERTICAL_PADDING}px ${$padding}px`
      : `${$padding}px ${VERTICAL_PADDING}px`};
    box-sizing: border-box;
    position: relative;
  `}
`;

const ElementsWrapper = styled.div<{
  _hasLink: boolean;
  _flexWrap: boolean;
  _font: FontValue;
  _isMobile: boolean;
  _closeButton?: boolean;
}>`
  position: relative;
  font-family: ${({ _font }) => _font?.value};
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  flex-wrap: ${({ _flexWrap }) => (_flexWrap ? 'wrap' : 'nowrap')};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ _isMobile }) => (_isMobile ? '8px' : '12px')};

  padding: 0
    ${({ _closeButton, _isMobile }) =>
      _closeButton ? (_isMobile ? '32px' : '44px') : '10px'};

  cursor: ${({ _hasLink }) => (_hasLink ? 'pointer' : 'auto')};
`;
